import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItemButton,
  Divider,
  useMediaQuery,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import BusinessIcon from '@mui/icons-material/Business';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ConstructionIcon from '@mui/icons-material/Construction';
import TableChartIcon from '@mui/icons-material/TableChart';
import { authAPI, clearSessionToken } from '../services/api';
import OrgSwitcher from './OrgSwitcher';
import { useOrg } from '../contexts/OrgContext';
import { useColorMode } from '../contexts/ColorModeContext';
import { useTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PolicyIcon from '@mui/icons-material/Policy';
import BrandLogo from './BrandLogo';
import { marketingLinks, FounderCredit } from './MarketingContent';
import MarketingMetadata from './MarketingMetadata';
import { smartDeployBrand } from '../theme/brand';

const PUBLIC_ROUTES = ['/vs', '/vs/vitruvi', '/vs/ineight', '/vs/spreadsheet-gis', '/pricing', '/about', '/customers', '/features', '/utility-construction-intelligence', '/', '/login', '/forgot-password', '/reset-password', '/terms', '/privacy', '/contact', '/landing-v2', '/what-we-do', '/bead-nepa'];
const DARK_PUBLIC_ROUTES = ['/vs', '/vs/vitruvi', '/vs/ineight', '/vs/spreadsheet-gis', '/pricing', '/about', '/customers', '/features', '/utility-construction-intelligence', '/', '/landing-v2', '/what-we-do', '/contact'];
const HIDE_PUBLIC_FOOTER_ROUTES = ['/', '/landing-v2'];

const SIDEBAR_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const brandColors = smartDeployBrand.colors;

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { toggleColorMode } = useColorMode();

  const isPublicPage = PUBLIC_ROUTES.includes(location.pathname);
  const isDarkPublicPage = DARK_PUBLIC_ROUTES.includes(location.pathname);
  const hidePublicFooter = HIDE_PUBLIC_FOOTER_ROUTES.includes(location.pathname);
  const showSidebar = currentUser && !isPublicPage && !isMobile;

  // Load user once on mount. Clear on /login navigation.
  const userLoadedRef = React.useRef(false);

  useEffect(() => {
    if (location.pathname === '/login') {
      setCurrentUser(null);
      userLoadedRef.current = false;
      return;
    }

    if (userLoadedRef.current) return; // Already loaded, skip

    const loadUser = async () => {
      try {
        const response = await authAPI.getMe({ skipAuthRedirect: true });
        setCurrentUser(response.data);
        userLoadedRef.current = true;
      } catch (err) {
        setCurrentUser(null);
      }
    };

    loadUser();
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      clearSessionToken();
      setCurrentUser(null);
      handleMenuClose();
      setMobileMenuOpen(false);
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      clearSessionToken();
    }
  };

  const handleOrgManage = () => {
    handleMenuClose();
    navigate('/organization');
  };

  const currentWidth = sidebarOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const { isOrgAdmin, getCurrentOrg, orgsLoaded, refreshOrganizations } = useOrg();
  const currentOrg = getCurrentOrg();
  const orgFeatures = currentOrg?.enabled_features || {};

  useEffect(() => {
    if (!currentUser || orgsLoaded) return;
    refreshOrganizations();
  }, [currentUser, orgsLoaded, refreshOrganizations]);

  // ───────── Main Nav Items ─────────
  // Feature-gated items only show when org data is loaded AND the feature is enabled.
  // While orgs are loading, feature-gated items are hidden to prevent a flash of all items.
  const mainNavItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Analysis Tools', icon: <AnalyticsIcon />, path: '/tools' },
    ...(orgFeatures.fielding ? [{ text: 'Field Ops', icon: <ConstructionIcon />, path: '/field' }] : []),
    ...(orgFeatures.permits ? [{ text: 'Permitting', icon: <MenuBookIcon />, path: '/permitting' }] : []),
    ...(orgFeatures.nepa_hub ? [{ text: 'NEPA BEAD', icon: <PolicyIcon />, path: '/nepa' }] : []),
    ...(orgFeatures.locate ? [{ text: 'Locate Data', icon: <LocationSearchingIcon />, path: '/locate' }] : []),
    ...(orgFeatures.tracking ? [{ text: 'Tracking', icon: <TableChartIcon />, path: '/tracking' }] : []),
    { text: 'Help', icon: <HelpOutlineIcon />, path: '/help' },
  ];

  if (currentUser?.is_admin) {
    mainNavItems.push({ text: 'Admin', icon: <SupervisorAccountIcon />, path: '/admin' });
  }

  const bottomNavItems = [
    { text: 'Profile', icon: <AccountCircleIcon />, path: '/profile' },
    ...(isOrgAdmin() ? [{ text: 'Organization', icon: <BusinessIcon />, path: '/organization' }] : []),
  ];


  // ───────── Nav Item Renderer ─────────
  const renderNavItem = (item, collapsed = false) => {
    const active = isActive(item.path);
    const button = (
      <ListItemButton
        key={item.text}
        onClick={() => { setMobileMenuOpen(false); navigate(item.path); }}
        sx={{
          minHeight: 44,
          justifyContent: collapsed ? 'center' : 'initial',
          px: collapsed ? 1.5 : 2.5,
          mx: 1,
          borderRadius: 1,
          mb: 0.3,
          bgcolor: active ? 'rgba(30, 136, 255, 0.16)' : 'transparent',
          color: active ? '#fff' : '#c8c8d4',
          '&:hover': {
            bgcolor: active ? 'rgba(30, 136, 255, 0.22)' : 'rgba(255, 255, 255, 0.06)',
          },
        }}
      >
        <ListItemIcon sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 2,
          justifyContent: 'center',
          color: active ? brandColors.brightBlue : '#8EA0B3',
        }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    );

    return collapsed ? (
      <Tooltip key={item.text} title={item.text} placement="right" arrow>
        {button}
      </Tooltip>
    ) : button;
  };

  // ───────── Desktop Sidebar Content ─────────
  const sidebarContent = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      bgcolor: brandColors.deepNavy,
      overflow: 'hidden',
    }}>
      {/* Logo / Brand */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: sidebarOpen ? 2.5 : 0,
        py: 2,
        justifyContent: sidebarOpen ? 'flex-start' : 'center',
        minHeight: 56,
      }}>
        <BrandLogo markSize={28} showWordmark={sidebarOpen} inverse compact sx={{ minWidth: 0 }} />
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1 }} />

      {/* Org Switcher */}
      {sidebarOpen && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <OrgSwitcher />
        </Box>
      )}

      {/* Main Navigation */}
      <List sx={{ flex: 1, pt: 1 }}>
        {mainNavItems.map(item => renderNavItem(item, !sidebarOpen))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 1 }} />

      {/* Bottom Navigation */}
      <List sx={{ py: 0.5 }}>
        {bottomNavItems.map(item => renderNavItem(item, !sidebarOpen))}

        {/* Dark mode toggle */}
        <Tooltip title={sidebarOpen ? '' : (theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode')} placement="right" arrow>
          <ListItemButton
            onClick={toggleColorMode}
            sx={{
              minHeight: 44,
              justifyContent: sidebarOpen ? 'initial' : 'center',
              px: sidebarOpen ? 2.5 : 1.5,
              mx: 1,
              borderRadius: 1,
              mb: 0.3,
              color: '#c8c8d4',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: sidebarOpen ? 2 : 0, justifyContent: 'center', color: '#8888a0' }}>
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText
                primary={theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* Logout */}
        <Tooltip title={sidebarOpen ? '' : 'Logout'} placement="right" arrow>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 44,
              justifyContent: sidebarOpen ? 'initial' : 'center',
              px: sidebarOpen ? 2.5 : 1.5,
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              color: '#c8c8d4',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.06)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 0, mr: sidebarOpen ? 2 : 0, justifyContent: 'center', color: '#8888a0' }}>
              <LogoutIcon />
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.875rem' }} />
            )}
          </ListItemButton>
        </Tooltip>
      </List>

      {/* Collapse Toggle */}
      <Box sx={{
        display: 'flex',
        justifyContent: sidebarOpen ? 'flex-end' : 'center',
        p: 1,
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <IconButton
          onClick={() => setSidebarOpen(!sidebarOpen)}
          size="small"
          sx={{ color: '#8888a0', '&:hover': { color: '#fff' } }}
        >
          {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  // ───────── Mobile Nav (temporary drawer) ─────────
  const mobileNav = (
    <Box sx={{ width: 280 }} role="presentation">
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BrandLogo markSize={28} compact inverse={isDarkPublicPage} />
      </Box>
      <Divider />
      {isPublicPage && <List>{marketingLinks.map(([label, href]) => <ListItemButton key={href} component={RouterLink} to={href} onClick={() => setMobileMenuOpen(false)}><ListItemText primary={label} /></ListItemButton>)}</List>}
      {currentUser && (
        <>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.dark' }}>
                {currentUser.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{currentUser.name}</Typography>
                <Typography variant="caption" color="text.secondary">{currentUser.email}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Organization
            </Typography>
            <OrgSwitcher />
          </Box>
          <Divider />
          <List>
            {mainNavItems.map(item => (
              <ListItemButton key={item.text} onClick={() => { setMobileMenuOpen(false); navigate(item.path); }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <List>
            {bottomNavItems.map(item => (
              <ListItemButton key={item.text} onClick={() => { setMobileMenuOpen(false); navigate(item.path); }}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
            <ListItemButton onClick={() => toggleColorMode()}>
              <ListItemIcon>
                {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </ListItemIcon>
              <ListItemText primary={theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
            </ListItemButton>
          </List>
          <Divider />
          <List>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </List>
        </>
      )}
      {!currentUser && (
        <List>
          <ListItemButton onClick={() => { setMobileMenuOpen(false); navigate('/login'); }}>
            <ListItemIcon><PersonIcon /></ListItemIcon>
            <ListItemText primary="Sign In" />
          </ListItemButton>
        </List>
      )}
    </Box>
  );

  // ───────── BARE LAYOUT (no app bars at all) ─────────
  if (location.pathname.startsWith('/forms/')) {
    return <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>{children}</Box>;
  }

  // ───────── PUBLIC LAYOUT (top bar, no sidebar) ─────────
  if (isPublicPage || !currentUser) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        bgcolor: isDarkPublicPage ? brandColors.deepNavyDark : 'background.default',
      }}>
        <AppBar position="sticky" elevation={0} sx={{
          borderBottom: '1px solid',
          borderColor: isDarkPublicPage ? 'rgba(230, 234, 240, 0.08)' : 'divider',
          backgroundColor: isDarkPublicPage ? brandColors.deepNavyDark : 'background.default',
          color: isDarkPublicPage ? brandColors.lightGray : 'text.primary',
        }}>
          <Toolbar sx={{ maxWidth: 'xl', width: '100%', mx: 'auto', px: { xs: 2, sm: 3 } }}>
            {isMobile && (
              <>
                <IconButton aria-label="Open navigation" color="inherit" edge="start" onClick={() => setMobileMenuOpen(true)} sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
              </>
            )}
            <Box
              component={RouterLink}
              to={currentUser ? "/dashboard" : "/"}
              sx={{ display: 'inline-flex', textDecoration: 'none', flexGrow: 1, minWidth: 0 }}
            >
              <BrandLogo markSize={28} compact inverse={isDarkPublicPage} />
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentUser ? (
                  <>
                    <Button component={RouterLink} to="/dashboard" sx={{ color: isDarkPublicPage ? brandColors.lightGray : 'text.primary' }}>Dashboard</Button>
                    <IconButton onClick={handleMenuOpen} color="inherit">
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
                        {currentUser.name?.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                  </>
                ) : (
                  <>
                    {marketingLinks.map(([label, href]) => <Button key={href} component={RouterLink} to={href} sx={{ color: isDarkPublicPage ? brandColors.lightGray : 'text.primary', fontWeight: 600 }}>{label}</Button>)}
                    <Button component={RouterLink} to="/login" variant="contained">Sign In</Button>
                  </>
                )}
              </Box>
            )}

            {isMobile && !currentUser && (
              <Button component={RouterLink} to="/login" variant="contained" size="small">Sign In</Button>
            )}
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          sx={{
            display: { md: 'none' },
            '& .MuiDrawer-paper': {
              backgroundColor: isDarkPublicPage ? brandColors.deepNavy : 'background.default',
              backgroundImage: 'none',
              color: isDarkPublicPage ? brandColors.lightGray : 'text.primary',
            }
          }}
        >
          {mobileNav}
        </Drawer>

        {/* User dropdown menu (public pages with logged-in user) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard'); }}>
            <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Dashboard" />
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>

        <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
          <MarketingMetadata />
          {children}
        </Box>

        {!hidePublicFooter && (
          <Box component="footer" sx={{
            py: 3, px: 2, mt: 'auto',
            borderTop: '1px solid',
            borderColor: isDarkPublicPage ? 'rgba(230, 234, 240, 0.08)' : 'divider',
            textAlign: 'center',
            bgcolor: isDarkPublicPage ? brandColors.deepNavyDark : 'background.default',
          }}>
            <Typography variant="body2" sx={{ color: isDarkPublicPage ? '#B5C0CC' : 'text.secondary' }}>
              © {new Date().getFullYear()} SmartDeploy - Network Engineering & Construction Suite
            </Typography>
            <FounderCredit />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: isDarkPublicPage ? '#B5C0CC' : 'text.secondary' }}>
                <RouterLink to="/terms" style={{ color: 'inherit', textDecoration: 'none', marginRight: 16 }}>
                  Terms of Service
                </RouterLink>
                {' | '}
                <RouterLink to="/privacy" style={{ color: 'inherit', textDecoration: 'none', marginLeft: 16 }}>
                  Privacy Policy
                </RouterLink>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // ───────── AUTHENTICATED LAYOUT (sidebar + content) ─────────
  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: currentWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
              borderRight: 'none',
              transition: 'width 250ms ease-in-out',
              overflowX: 'hidden',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Mobile hamburger bar + temporary drawer */}
      {isMobile && (
        <>
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.default',
            }}
          >
            <Toolbar variant="dense">
              <IconButton color="inherit" edge="start" onClick={() => setMobileMenuOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
              <Box
                component={RouterLink}
                to="/dashboard"
                sx={{ display: 'inline-flex', textDecoration: 'none', flexGrow: 1, minWidth: 0 }}
              >
                <BrandLogo markSize={26} compact />
              </Box>
              <IconButton onClick={handleMenuOpen} color="inherit">
                <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.dark', fontSize: '0.85rem' }}>
                  {currentUser.name?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
            </Toolbar>
          </AppBar>

          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            sx={{
              '& .MuiDrawer-paper': { backgroundColor: 'background.default', backgroundImage: 'none' }
            }}
          >
            {mobileNav}
          </Drawer>

          {/* Mobile avatar dropdown */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={currentUser.name} secondary={currentUser.email} />
            </MenuItem>
            <MenuItem onClick={handleOrgManage}>
              <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Organization" />
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
              <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Profile" />
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          ...(isMobile && { mt: '48px', pb: '56px' }), // offset for mobile AppBar and BottomNav
        }}
      >
        <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>

        {/* Footer (inside content area) - hide on mobile since BottomNav takes its place */}
        {!isMobile && (
          <Box component="footer" sx={{
            py: 0.5, px: 2,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} SmartDeploy - Network Engineering & Construction Suite
            </Typography>
          </Box>
        )}
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1100,
            pb: 'env(safe-area-inset-bottom)',
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider'
          }} 
          elevation={3}
        >
          <BottomNavigation
            showLabels
            value={location.pathname.split('/')[1] || 'dashboard'}
            onChange={(event, newValue) => {
              if (newValue === 'menu') {
                setMobileMenuOpen(true);
              } else {
                navigate(`/${newValue}`);
              }
            }}
            sx={{
              "& .MuiBottomNavigationAction-root": {
                color: 'text.secondary',
              },
              "& .Mui-selected": {
                color: 'primary.main',
              }
            }}
          >
            <BottomNavigationAction label="Dashboard" value="dashboard" icon={<DashboardIcon />} />
            <BottomNavigationAction label="Tools" value="tools" icon={<AnalyticsIcon />} />
            <BottomNavigationAction label="Permits" value="permitting" icon={<MenuBookIcon />} />
            <BottomNavigationAction label="Tracking" value="tracking" icon={<TableChartIcon />} />
            <BottomNavigationAction label="Menu" value="menu" icon={<MoreHorizIcon />} />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}

export default Layout;
